import {
  Box,
  Button,
  CssBaseline,
  Grid,
  TextField,
  ThemeProvider,
  Typography
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { debounce } from 'lodash';
import { Auth } from 'aws-amplify';
import useDeletedNotification from './useDeletedNotification';
import NotificationAccordion from './NotificationAccordion';
import themev5 from '../../../themev5';
import styles from './styles';
import HowTo from './HowTo';

const GuidanceDeleteNotification = () => {
  const [showPreviousNotifications, setShowPreviousNotifications] = useState<boolean>(false);
  const [searchText, setSearchtext] = useState<string>('');
  const {
    getRecentNotifications,
    recentNotifications,
    getNotificationsToBeQced,
    toQCList,
    updateNotificationRecord,
    searchThroughRecentRecords
  } = useDeletedNotification();

  useEffect(() => {
    getRecentNotifications();
    getNotificationsToBeQced();
  }, []);

  const filterRecentNotifications = useMemo(() => {
    return searchThroughRecentRecords(searchText);
  }, [searchText, searchThroughRecentRecords]);

  const debouncedSearch = useCallback(
    debounce(text => {
      setSearchtext(text);
    }, 500),
    []
  );

  const onEditSave = async ({
    id,
    notificationCanBeSent,
    qcNotes,
    notificationType,
    incorrectAvailableAtSource,
    guidancePrimaryKey
  }: any) => {
    const user = await Auth.currentUserInfo();
    const curator = user.attributes['custom:user'];
    const payload = {
      notification_can_be_sent: notificationCanBeSent,
      qc_notes: qcNotes,
      notification_type: notificationType,
      incorrect_available_at_source_flag: incorrectAvailableAtSource,
      qc_status: true,
      guidance_pk: guidancePrimaryKey,
      curator
    };
    await updateNotificationRecord(id, payload);
    getRecentNotifications();
    getNotificationsToBeQced();
  };

  return (
    <ThemeProvider theme={themev5}>
      <CssBaseline />
      <Box p={6} pt={10} position='relative'>
        <HowTo />
        <Button
          onClick={() => setShowPreviousNotifications(prev => !prev)}
          sx={styles.showRecentNotificationButton}>
          {showPreviousNotifications ? 'Hide' : 'Show recently sent notifications'}
        </Button>
        <Grid container spacing={4} height='100%' overflow='hidden'>
          <Grid
            item
            xs={showPreviousNotifications ? 6 : 9}
            position='relative'
            overflow='hidden'
            mx='auto'>
            {toQCList?.length === 0 && (
              <Typography component='h3' sx={styles.noResults}>
                No entries to curate
              </Typography>
            )}
            {toQCList?.length > 0 && (
              <Typography component='h3' fontWeight={600} mb={2} textAlign='center'>
                Deleted Status Needs to be curated
              </Typography>
            )}
            <Box height='81vh' overflow='auto'>
              {toQCList?.map(notification => (
                <NotificationAccordion
                  key={notification.id}
                  title={notification.metadata.title}
                  titleLink={notification.metadata.title_link}
                  pdfUrl={notification.metadata.field_associated_media_2 || '--'}
                  issueDate={notification.metadata.field_issue_datetime}
                  notificationType={notification.notiticationType}
                  notificationSentStatus={notification.notificationSentStatus}
                  editable
                  onEditSave={onEditSave}
                  availableAtSource={notification.available_at_source}
                  incorrectAvailableAtSource={notification.incorrect_available_at_source_flag}
                  id={notification.id}
                  date={notification.created_at} // for to be curated entries needs to be sorted based on the created date.
                  guidancePrimaryKey={notification.guidance_pk}
                  s3Path={notification.metadata.s3_file_path}
                />
              ))}
            </Box>
          </Grid>
          {showPreviousNotifications && (
            <Grid item xs={5} m='auto' position='relative' overflow='hidden'>
              {recentNotifications?.length === 0 && (
                <Typography component='h3' sx={styles.noResults}>
                  No recent notifications.
                </Typography>
              )}
              {recentNotifications?.length > 0 && (
                <>
                  <Typography component='h3' fontWeight={600} mb={2} textAlign='center'>
                    Previously sent notifications
                  </Typography>
                  <TextField
                    placeholder='Search in recently sent notification.'
                    onChange={event => debouncedSearch(event.target.value)}
                    fullWidth
                  />
                </>
              )}
              <Box height='calc(81vh - 56px)' overflow='auto'>
                {filterRecentNotifications?.map(notification => (
                  <NotificationAccordion
                    key={notification.guidance_pk}
                    title={notification.title}
                    titleLink={notification.title_link}
                    pdfUrl={notification.field_associated_media_2 || '--'}
                    issueDate={notification.field_issue_datetime}
                    notificationType={notification.notiticationType}
                    notificationSentStatus={notification.notificationSentStatus}
                    incorrectAvailableAtSource={notification.incorrect_available_at_source_flag}
                    editable={
                      notification.notificationSentStatus === 'Queued' ||
                      notification.notificationSentStatus === 'Rejected'
                    }
                    onEditSave={onEditSave}
                    availableAtSource={notification.available_at_source}
                    notificationCanbeSent={notification.notification_can_be_sent}
                    id={notification.id}
                    curatorNotes={notification.curation_notes}
                    date={notification.notification_can_be_sent_updated_at} // notification_can_be_sent_updated_at gets updated when curators does the curation. Then only we should allow for the curation.
                    guidancePrimaryKey={notification.guidance_pk}
                    s3Path={notification.s3_file_path}
                  />
                ))}
              </Box>
            </Grid>
          )}
        </Grid>
      </Box>
    </ThemeProvider>
  );
};

export default React.memo(GuidanceDeleteNotification);
